/** Pure formatting helpers for the CRM tab. Search hits carry epoch SECONDS; SQL
 * surfaces return ISO strings — both normalize through fmtShortDate.
 *
 * ⛔ EVERY DATE HERE IS ONE NORMALISED INSTANT RENDERED IN ONE ZONE (timeline-row-layout-spec §3).
 * The backend serialises naive UTC WITHOUT a `Z` (`datetime.isoformat()` on a naive column — verified
 * on `entity_timeline`'s `occurred_at`), and `new Date(<naive iso>)` parses that as BROWSER-LOCAL, so
 * an email sent at 02:00 UTC printed "Sep 19" to a UTC reader and should have read "Sep 18" to an
 * eastern one. Normalisation is `ensureUtc` from `formatTimestamp.ts` — imported, never re-copied —
 * and the render zone is ET, the house rule that file states. A row's date and its time are computed
 * from the same instant in the same zone so they can never disagree.
 */
import { ensureUtc } from '../../utils/formatTimestamp';
import type { CrmTimelineRow } from '../../types';

/** The house zone (`formatTimestamp.ts`). A `tz` parameter exists on the exported helpers only so a
 *  test can pin a zone; production always takes this default. */
const ET = 'America/New_York';

/** ⛔ A DATE-ONLY STRING IS A WALL DATE, NEVER AN INSTANT (spec §3). A `date` cell's value is
 *  stored date-only — `lists._coerce_cell_value` does `s.split("T")[0]` — so `2026-10-01` means the
 *  first of October wherever you read it, not a moment that can fall on either side of a midnight.
 *  Parsed as an instant it is UTC midnight, and rendering THAT in ET prints "Sep 30": a target date
 *  a day early on every list grid and record panel. */
const WALL_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isWallDate(value: string | number | null | undefined): boolean {
  return typeof value === 'string' && WALL_DATE.test(value.trim());
}

/** The zone a value must be RENDERED in. A wall date is pinned to UTC — the zone it was PARSED in —
 *  so the day survives the round trip; everything else takes the caller's zone. Chosen over
 *  constructing a browser-local midnight, which only moves the failure onto a non-ET reader. */
function zoneFor(value: string | number | null | undefined, tz: string): string {
  return isWallDate(value) ? 'UTC' : tz;
}

function toDate(value: string | number | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const d = typeof value === 'number' ? new Date(value * 1000) : ensureUtc(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** The calendar year this instant falls in *in `tz`* — not `getFullYear()`, which answers for the
 *  browser's zone and would drop the year from a 31 Dec 23:00 ET row for a UTC reader. */
function yearIn(d: Date, tz: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric' }).format(d);
}

/** 'Jul 1' within the current year, 'Jul 1, 2025' otherwise, '—' when absent/invalid — rendered in
 *  `tz` (default ET) from the `ensureUtc`-normalised instant, or in UTC for a wall date. */
export function fmtShortDate(value: string | number | null | undefined, tz: string = ET): string {
  const d = toDate(value);
  if (!d) return '—';
  const zone = zoneFor(value, tz);
  // The current-year test uses the SAME zone as the render, so a wall date cannot gain a year it
  // would not print with.
  const opts: Intl.DateTimeFormatOptions =
    yearIn(d, zone) === yearIn(new Date(), tz)
      ? { month: 'short', day: 'numeric', timeZone: zone }
      : { month: 'short', day: 'numeric', year: 'numeric', timeZone: zone };
  return d.toLocaleDateString('en-US', opts);
}

/**
 * Date + time for a timeline row, from ONE normalised instant (timeline-row-layout-spec §3).
 *
 * The two halves are deliberately computed from the same `Date` in the same `tz`: a row that prints
 * "Sep 18" over "2:00 AM" is worse than either error alone, and that is precisely what two helpers
 * with two normalisations produce.
 *
 * The time carries a LITERAL ` ET` label, never `timeZoneName: 'short'` — the house rule in
 * `formatTimestamp.ts`: one label year-round, because EST in January and EDT in July reads to
 * operators as two different clocks. A caller that pins another zone (tests only) gets no label
 * rather than a false one.
 *
 * Absent / invalid → `{ date: '—', time: '' }`; the caller omits an empty time span. A WALL DATE
 * (`2026-10-01`) has no time of day at all, so it returns an empty `time` rather than the midnight
 * its parse happens to land on — there is no clock in the value to print.
 */
export function fmtRowWhen(
  value: string | number | null | undefined, tz: string = ET,
): { date: string; time: string } {
  const d = toDate(value);
  if (!d) return { date: '—', time: '' };
  const date = fmtShortDate(value, tz);
  if (isWallDate(value)) return { date, time: '' };   // a wall date has no clock to print
  const clock = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
  return { date, time: tz === ET ? `${clock} ET` : clock };
}

/** Bidirectional relative day for the L3b derived date columns: past → '3d ago' / '2w ago', future →
 * 'in 2d' / 'in 3w', 'today', '—' when absent. (fmtRecency is past-only; next_meeting is future.) */
export function fmtRelativeDay(value: string | null | undefined): string {
  const d = toDate(value ?? null);
  if (!d) return '—';
  const days = Math.round((d.getTime() - Date.now()) / 86_400_000);
  const mag = Math.abs(days);
  if (mag === 0) return 'today';
  const unit = mag < 14 ? `${mag}d` : mag < 60 ? `${Math.floor(mag / 7)}w`
    : mag < 365 ? `${Math.floor(mag / 30)}mo` : `${Math.floor(mag / 365)}y`;
  return days < 0 ? `${unit} ago` : `in ${unit}`;
}

/** Compact recency: '3d' / '2w' / '4mo' / '1y+', '—' when absent. */
export function fmtRecency(value: string | null | undefined): string {
  const d = toDate(value ?? null);
  if (!d) return '—';
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
  if (days < 1) return 'today';
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.floor(days / 7)}w`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return '1y+';
}

const label = (p: { display_name: string | null; email_address: string | null }) =>
  p.display_name || p.email_address || '?';

/** True for a real individual (missing/'person'); false for a DL / shared / automated mailbox. */
export function isPersonSubtype(subtype: string | null | undefined): boolean {
  return !subtype || subtype === 'person';
}

/** Short chip label for a non-person directory subtype. */
export function subtypeLabel(subtype: string | null | undefined): string {
  switch (subtype) {
    case 'distribution_list':
      return 'list';
    case 'shared_mailbox':
      return 'shared';
    case 'automated':
      return 'automated';
    default:
      return 'list';
  }
}

/** Email participants split by role for the timeline's "involved entities" line (E3). Names prefer
 *  the display name, then the address. */
export function emailParties(row: CrmTimelineRow): { from: string | null; to: string[]; cc: string[] } {
  const parts = row.participants ?? [];
  const pick = (role: string) =>
    parts.filter((p) => p.role === role).map((p) => p.display_name || p.email_address || '?');
  return { from: pick('from')[0] ?? null, to: pick('to'), cc: pick('cc') };
}

/** Meeting attendee display names (E3). Empty when the payload carried only a count. */
export function meetingAttendeeNames(row: CrmTimelineRow): string[] {
  return (row.attendees ?? []).map((a) => a.display_name || a.email_address || '?');
}

/** 'A, B, +N more' — first `max` names then an overflow tail. */
export function clipNames(names: string[], max = 3): string {
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')}, +${names.length - max} more`;
}

/** One-line who-summary for a timeline row: 'Jane → Alice, Bob' / 'N attendees' / a note's author. */
export function participantSummary(row: CrmTimelineRow): string {
  if (row.kind === 'note') {
    return row.author_name ?? 'Someone';
  }
  if (row.kind === 'meeting') {
    const n = row.attendee_count ?? row.attendees?.length ?? 0;
    return `${n} attendee${n === 1 ? '' : 's'}`;
  }
  const parts = row.participants ?? [];
  const from = parts.find((p) => p.role === 'from');
  const rcpt = parts.filter((p) => p.role === 'to' || p.role === 'cc');
  const names = rcpt.slice(0, 2).map(label);
  const more = rcpt.length > 2 ? '…' : '';
  if (!from && !rcpt.length) return '';
  return `${from ? label(from) : '?'} → ${names.join(', ')}${more}`;
}

/** Turn a stored domain (`acme.com`, `www.acme.com`, `https://acme.com/x`) into a safe external href,
 * or null when the value is not a plain hostname-shaped string — the caller then renders plain text,
 * so a malformed or attacker-shaped value never becomes a link. */
export function domainHref(v: string | null | undefined): string | null {
  const s = (v || '').trim().toLowerCase();
  if (!s) return null;
  if (/^https?:\/\/[a-z0-9-]+(\.[a-z0-9-]+)+([/?#]|$)/.test(s)) return s;
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(s)) return `https://${s}`;
  return null;
}
